import { NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { Data } from '@singularsystems/neo-core';
import { AppService, Types } from '../../../App/Services/AppService';
import { reaction } from 'mobx';
import StaticEventLookup from '../../../DataInterception/EventMapping/Models/StaticEventSearchModal/StaticEventLookup';
import CreateStaticEventCommand from '../Models/Commands/CreateStaticEventCommand';
import UpdateStaticEventCommand from '../Models/Commands/UpdateStaticEventCommand';
import { TextConstants } from '../../../Common/TextConstants';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import EventSearchCriteria from '../../../DataInterception/EventMapping/Models/EventSearchCriteria';
import ManageStaticEventComponentVM from '../Components/ManageStaticEventComponentVM';

@NeoModel
export default class StaticEventsVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        public staticEventsApiClient = AppService.get(Types.ApiClients.StaticEventsApiClient),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {
        super(taskRunner);
    }
    public showManageStaticEventComponent: boolean = false;
    public showDeleteEventModal: boolean = false;
    public deletedStaticEvent = new StaticEventLookup();
    public eventSearchCriteria = new EventSearchCriteria();
    public manageStaticEventComponentVM = new ManageStaticEventComponentVM();
    public isFilterExpanded = false;

    public pageManager = new Data.PageManager(this.eventSearchCriteria, StaticEventLookup, this.staticEventsApiClient.getPagedStaticEvents, {
        pageSize: 10,
        sortAscending: false,
        fetchInitial: true,
        initialTaskRunner: this.taskRunner,
    });

    public async initialise() {
        reaction(() => this.eventSearchCriteria.searchCriteria, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isBuyAtBookCostEventInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isCashNotCapitalisedExclInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isIWTInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isCapitalGainEventInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isCashFlowInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isCorporateActionInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isDWTInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isDividendEventInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isInterestInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isProcessedAsNegativeBuyInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isQuantityNotValueInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isSellAtBookCostEventInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isTransferInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
        reaction(() => this.eventSearchCriteria.isZeroPriceEventInd, () => this.pageManager.refreshData(), {fireImmediately: true, delay: 650 });
    }

    public addStaticEvent() {
        this.showManageStaticEventComponent = true;
    }

    public cancel() {
        this.manageStaticEventComponentVM.managedStaticEvent = new StaticEventLookup();
        this.showManageStaticEventComponent = false;
    }

    public cancelDeleteStaticEvent() {
        this.manageStaticEventComponentVM.managedStaticEvent = new StaticEventLookup();
        this.showDeleteEventModal = false;
    }

    public async clearSearch() {
        this.eventSearchCriteria.searchCriteria = "";

        this.eventSearchCriteria.isBuyAtBookCostEventInd =
            this.eventSearchCriteria.isCashNotCapitalisedExclInd =
            this.eventSearchCriteria.isIWTInd =
            this.eventSearchCriteria.isCapitalGainEventInd =
            this.eventSearchCriteria.isCashFlowInd = 
            this.eventSearchCriteria.isCorporateActionInd = 
            this.eventSearchCriteria.isDWTInd = 
            this.eventSearchCriteria.isDividendEventInd =
            this.eventSearchCriteria.isInterestInd =
            this.eventSearchCriteria.isProcessedAsNegativeBuyInd =
            this.eventSearchCriteria.isQuantityNotValueInd = 
            this.eventSearchCriteria.isSellAtBookCostEventInd =
            this.eventSearchCriteria.isTransferInd =
            this.eventSearchCriteria.isZeroPriceEventInd = false;

        await this.pageManager.refreshData();
    }

    public deleteStaticEvent() {
        if (this.deletedStaticEvent.isDeletedInd === true) {
            this.taskRunner.run(async () => {
                var response = await this.staticEventsApiClient.updateStaticEvent((this.mapEventToUpdateCommand(this.deletedStaticEvent)!.toJSObject()))
                if (response.data.success) {
                    this.showDeleteEventModal = false;
                    NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.EventDeleted, NotificationDuration.Standard);
                    await this.pageManager.refreshData();
                }
                else {
                    NotifyUtils.addDanger(TextConstants.Titles.Error, response.data.error, NotificationDuration.Long);
                    this.cancelDeleteStaticEvent();
                }
            });
        }
    }

    public displayDeleteEventModal(staticEventLookup: StaticEventLookup){
        this.showDeleteEventModal = true;
        this.deletedStaticEvent = new StaticEventLookup();
        this.deletedStaticEvent.mapFrom(staticEventLookup).toJSObject();
        this.deletedStaticEvent.isDeletedInd = true;
    }

    public editEvent(staticEventLookup: StaticEventLookup) {
        this.manageStaticEventComponentVM.managedStaticEvent.mapFrom(staticEventLookup).toJSObject();
        this.showManageStaticEventComponent = true;
    }

    private mapEventToCreateCommand(staticEventLookup: StaticEventLookup) {
        var command = new CreateStaticEventCommand().mapFrom(staticEventLookup);
        return command;
    }

    private mapEventToUpdateCommand(staticEventLookup: StaticEventLookup) {
        var command = new UpdateStaticEventCommand().mapFrom(staticEventLookup);
        command.cashNotCapitalisedExclInd = staticEventLookup.isCashNotCapitalisedExclInd;
        command.isIWTInd = staticEventLookup.isIWTInd;
        command.staticEventID = staticEventLookup.staticEventID;
        return command;
    }

    public async saveStaticEvent() {
        this.taskRunner.run(async () => {
            var response = this.manageStaticEventComponentVM.managedStaticEvent.staticEventID > 0
                ? await this.staticEventsApiClient.updateStaticEvent((this.mapEventToUpdateCommand(this.manageStaticEventComponentVM.managedStaticEvent)!.toJSObject()))
                : await this.staticEventsApiClient.createStaticEvent((this.mapEventToCreateCommand(this.manageStaticEventComponentVM.managedStaticEvent)!.toJSObject()));

            if (response.data.success) {
                NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.EventSaved, NotificationDuration.Standard);
                await this.pageManager.refreshData();
            }
            else {
                NotifyUtils.addDanger(TextConstants.Titles.Error, response.data.error, NotificationDuration.Long);
            }
            this.manageStaticEventComponentVM.managedStaticEvent = new StaticEventLookup();
            this.showManageStaticEventComponent = false;
        });
    }
}